import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  TRANSITIONS_V2_DATA,
  TransitionsEntityKind,
  type TransitionsFiltersV2,
} from '@revelio/data-access';

import { applyGeoFilter } from '../utils/applyGeoFilter';
import type {
  GeoLevel,
  ResolvedProfileIds,
  RoleGranularity,
  RoleLevel,
} from './useResolveProfileIds';

export interface AdjacentRole {
  id: number;
  shortName: string;
  longName: string;
  transitionCount: number;
  transitionShare: number;
}

interface UseAdjacentRolesResult {
  adjacentRoles: AdjacentRole[];
  /** The role level actually used for the query (for display context) */
  queryRoleLevel: RoleLevel | null;
  isLoading: boolean;
  error: string | null;
}

const ROLE_GRANULARITY_TO_ENTITY: Record<
  RoleGranularity,
  TransitionsEntityKind
> = {
  role_k15000: TransitionsEntityKind.RoleK15000,
  role_k1500: TransitionsEntityKind.RoleK1500,
  role_k150: TransitionsEntityKind.RoleK150,
  role_k7: TransitionsEntityKind.RoleK7,
};

/**
 * Pick the best role level for transitions queries.
 * Prefers role_k1500 for a good balance of granularity and data density.
 * Falls back to role_k150 if k1500 isn't available.
 * Avoids role_k15000 (too sparse for transitions) and role_k7 (too broad).
 */
const pickBestRoleLevel = (roleLevels: RoleLevel[]): RoleLevel | null => {
  // Prefer k1500 — granular enough to be useful, dense enough to have data
  const k1500 = roleLevels.find((l) => l.type === 'role_k1500');
  if (k1500) return k1500;

  // Fall back to k150
  const k150 = roleLevels.find((l) => l.type === 'role_k150');
  if (k150) return k150;

  // Last resort: finest available (but skip k7)
  return roleLevels.find((l) => l.type !== 'role_k7') ?? null;
};

export const useAdjacentRoles = (
  resolvedIds: ResolvedProfileIds,
  selectedGeo: GeoLevel | null
): UseAdjacentRolesResult => {
  const queryRoleLevel = useMemo(
    () => pickBestRoleLevel(resolvedIds.roleLevels),
    [resolvedIds.roleLevels]
  );

  const variables = useMemo(() => {
    if (!queryRoleLevel) return undefined;

    const entityType =
      ROLE_GRANULARITY_TO_ENTITY[queryRoleLevel.type] ??
      TransitionsEntityKind.RoleK150;

    const filters: TransitionsFiltersV2 = {};
    if (selectedGeo) {
      applyGeoFilter(filters, selectedGeo);
    }

    return {
      req: {
        entities: [
          {
            EntID: queryRoleLevel.id,
            EntType: entityType,
          },
        ],
        filters,
      },
    };
  }, [queryRoleLevel, selectedGeo]);

  const [{ data, fetching, error }] = useQuery({
    query: TRANSITIONS_V2_DATA,
    variables: variables ?? {
      req: { entities: [], filters: {} },
    },
    pause: !variables,
  });

  const adjacentRoles = useMemo((): AdjacentRole[] => {
    const entities = data?.transitionsV2?.entities;
    if (!entities || entities.length === 0) return [];

    const firstEntity = entities[0];
    if (!firstEntity) return [];

    const outflows = firstEntity.metrics?.flows?.outflows;
    if (!outflows) return [];

    // Exclude the user's own role (by ID)
    const currentRoleId = queryRoleLevel?.id ?? null;

    return outflows
      .filter(
        (flow): flow is NonNullable<typeof flow> =>
          flow !== null && flow !== undefined
      )
      .filter((flow) => {
        // Filter out unknown/empty entries
        const name = (flow.long_name || flow.short_name || '').toLowerCase();
        if (name === 'unknown' || name === 'empty' || name === '') return false;

        // Filter out the user's current role
        if (currentRoleId !== null && flow.id === currentRoleId) return false;

        return true;
      })
      .map((flow) => ({
        id: flow.id,
        shortName: flow.short_name ?? '',
        longName: flow.long_name ?? '',
        transitionCount: flow.count ?? 0,
        // API returns share as a percentage (e.g. 6.07 = 6.07%), normalize to 0-1
        transitionShare: (flow.share ?? 0) / 100,
      }))
      .sort((a, b) => b.transitionShare - a.transitionShare);
  }, [data, queryRoleLevel?.id]);

  return {
    adjacentRoles,
    queryRoleLevel,
    isLoading: fetching,
    error: error ? 'Failed to load career transitions' : null,
  };
};
